import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Typography } from "@thingsw/pitta-design-system";
import Input from "@thingsw/pitta-design-system/dist/components/Input";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useMediaQuery from "@material-ui/core/useMediaQuery";

import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import { RootState } from "../../features/store";
import {
  loadSubscription,
  loadTransactions,
  loadWebSubscription,
  PAYMENT,
} from "../../features/Payment/slice";
import axios from "axios";
import { USER } from "../../features/User/slice";
import moment from "moment";
import { openToast } from "../../features/Toast/slice";
import { handleServer500, setError } from "../../features/Error/slice";
import {
  Webviewer,
  FLEET_PRICE,
  FLEET_EXTRA,
  API_GATEWAY_URI,
  STRIPE_OS_TYPE,
  RESULT_CODE,
  LightColors,
  MAX_CAM_COUNT,
} from "@thingsw/pitta-modules";

interface SubMgrModalProps {
  open: boolean;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  onClickNegative?: () => void;
  onClickPositive?: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    [theme.breakpoints.up(Webviewer.mobile)]: {
      maxWidth: 438,
    },
  },
  plan: {
    paddingBottom: 24,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      paddingBottom: 0,
    },
  },
}));

export const SubMgrModal = ({
  open,
  onClose,
  onClickNegative,
  onClickPositive,
}: SubMgrModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const classes = useStyles();
  const dispatch = useDispatch();

  const { email, loginInfo } = useSelector((state: RootState) => state[USER]);

  const subscriptionInfo = useSelector(
    (state: RootState) => state[PAYMENT].subscriptionInfo
  ); /*Redux 사용*/

  const [camCnt, setCamCnt] = useState<number>(
    subscriptionInfo?.cameraLimit ?? 1
  );
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const [disabledChange, setDisabledChange] = useState(true);
  const [loading, setLoading] = useState(false);

  const totalPrice = useMemo(
    () => (FLEET_PRICE + (camCnt - 1) * FLEET_EXTRA).toFixed(2),
    [camCnt]
  );

  const loadSubscriptionInfo = useCallback(() => {
    dispatch(loadSubscription());
    dispatch(loadWebSubscription());
    dispatch(loadTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (camCnt) {
      setDisabledChange(subscriptionInfo?.cameraLimit === camCnt);
    }
  }, [camCnt, subscriptionInfo]);

  const handleUpdateCam = useCallback(async () => {
    setLoading(true);

    try {
      const resp1 = await axios.post(
        `${API_GATEWAY_URI}/Payment/WebSubscribeCameraChange`,
        {
          email,
          user_token: loginInfo?.user_token,
          ostype: STRIPE_OS_TYPE,
          step: "payload",
          itemID: `fleet${camCnt}`,
          tokenType: "web",
        }
      );

      const data1 = resp1.data as {
        resultcode: RESULT_CODE;
        response: { payload: string };
      };

      if (data1.resultcode === "BC_ERR_OK") {
        const resp2 = await axios.post(
          `${API_GATEWAY_URI}/Payment/WebSubscribeCameraChange`,
          {
            email,
            user_token: loginInfo?.user_token,
            ostype: STRIPE_OS_TYPE,
            step: "verify",
            payload: data1.response.payload,
            cameraCount: camCnt,
            tokenType: "web",
          }
        );

        const data2 = resp2.data as {
          resultcode: RESULT_CODE;
        };

        if (data2.resultcode === "BC_ERR_OK") {
          setLoading(false);
          onClickPositive?.();

          loadSubscriptionInfo();
          dispatch(openToast({ message: "Changes saved" }));
        } else if (data2.resultcode === "BC_ERR_INTERNAL_ERROR") {
          dispatch(handleServer500());
        } else {
          dispatch(setError("An error occured while_"));
        }
      } else if (data1.resultcode === "BC_ERR_INTERNAL_ERROR") {
        dispatch(handleServer500());
      } else {
        dispatch(setError("An error occured while_"));
      }
    } catch (err) {
      console.error(err);
      dispatch(setError("An error occured while_"));
    } finally {
      setLoading(false);
      onClickNegative?.();
    }
  }, [
    camCnt,
    dispatch,
    email,
    loadSubscriptionInfo,
    loginInfo?.user_token,
    onClickNegative,
    onClickPositive,
  ]);

  return (
    <Modal
      open={open}
      mobile={mobile}
      onClose={onClose}
      onClickNegative={onClickNegative}
      onClickPositive={() => handleUpdateCam()}
      className={classes.root}
      heading={t("Manage cameras")}
      close={true}
      fullSizeSub={mobile}
      content={
        <div style={{}}>
          <Typography
            category="Default"
            variant="Small"
            htmlColor={LightColors.primary["2"]}
          >
            {t("You manage_available", { a: camCnt, b: MAX_CAM_COUNT })}
          </Typography>
          <div
            style={{
              margin: "16px 0 32px",
              width: "100%",
              height: 119,
              backgroundColor: `${LightColors.primary["1"]}08`,
              zIndex: 0,
              borderRadius: 4,
            }}
          >
            <div style={{ padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  paddingBottom: 8,
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{ zIndex: 1 }}
                  category="Default"
                  variant="Small"
                >
                  {t("Subscription")}
                </Typography>
                <Typography
                  category="Default"
                  variant="Small"
                  style={{ textAlign: "right" }}
                >
                  {FLEET_PRICE} USD/mo
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  paddingBottom: 8,
                  justifyContent: "space-between",
                }}
              >
                <Typography category="Default" variant="Small">
                  {t("Extra cameras")}
                </Typography>
                <Typography
                  category="Default"
                  variant="Small"
                  style={{ textAlign: "right" }}
                >
                  {((camCnt - 1) * FLEET_EXTRA).toFixed(2)} USD/mo
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  borderBottom: `1px solid ${LightColors.primary["5"]}`,
                }}
              ></div>
              <div
                style={{
                  display: "flex",
                  paddingTop: 8,
                  justifyContent: "space-between",
                }}
              >
                <Typography category="Default" variant="Small">
                  {t("Total due")}{" "}
                  {moment(
                    subscriptionInfo?.endOfMonthDT,
                    "YYYY-MM-DD hh:mm:ss"
                  ).format("MMM D, YYYY")}
                </Typography>
                <Typography
                  category="Default"
                  variant="Small"
                  style={{ textAlign: "right" }}
                >
                  {totalPrice} USD/mo
                </Typography>
              </div>
            </div>
          </div>
          <div>
            <Input
              PlusButtonDis={camCnt === MAX_CAM_COUNT}
              MinusButtonDis={camCnt === 1}
              label="Cameras"
              count
              inputmode="numeric"
              pattern="[0-9]*"
              value={camCnt}
              onPlus={() => {
                setCamCnt((c) => Math.min(c + 1, MAX_CAM_COUNT));
              }}
              onMinus={() => {
                setCamCnt((c) => Math.max(c - 1, 1));
              }}
              onChange={(event) => {
                console.log(event.target.value);
                if (event.target.value === "") {
                  setCamCnt(1);
                } else {
                  const value = Number.parseInt(event.target.value);
                  console.log("aaa", value);
                  if (value <= MAX_CAM_COUNT && value > 0) {
                    setCamCnt(value);
                  }
                }
                setDisabledChange(false);
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                className={classes.plan}
                style={{
                  width: "94%",
                  display: "flex",
                }}
              >
                <Typography
                  style={{ paddingTop: 3 }}
                  category="Default"
                  variant="Caption"
                  htmlColor={LightColors.primary["2"]}
                >
                  {t("available on your_", { a: MAX_CAM_COUNT })}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      }
      LButton={t("Cancel")}
      RButton={t("Change")}
      RButtonDisabled={disabledChange}
      Secondary={false}
      loading={loading}
    />
  );
};
